import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import GooglePlacePriceLevel from 'tripPlanner/components/GooglePlacePriceLevel'
import type { TripListPrice } from './tripListTypes'

const LuxPlusLabel = styled(Caption)`
  color: ${props => props.theme.palette.product.luxPlus.foreground};
`

const LuxPlusAmount = styled(BodyText)`
  color: ${props => props.theme.palette.product.luxPlus.foreground};
`

const StandardAmount = styled(BodyText)`
  &.is-muted {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
    text-decoration: line-through;
    text-decoration-thickness: ${rem(1)};
  }
`

const SaleUnitCaption = styled(Caption)`
  font-weight: ${props => props.theme.font.primary.weight.regular};
`

interface Props {
  price: TripListPrice
  luxPlusEnabled: boolean
}

function TripListRowPrice({ price, luxPlusEnabled }: Props) {
  const intl = useIntl()

  if (price.price === undefined || !price.currency) {
    return price.priceLevel ? <GooglePlacePriceLevel priceLevel={price.priceLevel} /> : null
  }

  const showLuxPlusPrice = luxPlusEnabled && price.memberPrice !== undefined && price.memberPrice < price.price

  const fromLabel = intl.formatMessage({
    id: '2VC6F87d4jwCnSMVMP2i',
    defaultMessage: 'From',
    description: 'Price prefix shown above list row prices in the trip planner list view',
  })

  const saleUnit = price.saleUnit
    ? intl.formatMessage(
        {
          id: 'fcmSbY6UVkeqwZUWFxn3',
          defaultMessage: '/{saleUnit}',
          description: 'Sale unit suffix shown after a formatted price in the trip planner list row',
        },
        { saleUnit: price.saleUnit },
      )
    : undefined

  if (showLuxPlusPrice) {
    return (
      <Group direction="vertical" gap={2}>
        <LuxPlusLabel variant="large" weight="bold">
          {intl.formatMessage({
            id: 'rbg4Q6e_D8FVcN4JaDyh',
            defaultMessage: 'LUXPLUS+',
            description: 'LuxPlus label shown above member prices in the trip planner list row',
          })}
        </LuxPlusLabel>
        <LuxPlusAmount variant="medium" weight="bold">
          <FormatCurrency value={price.memberPrice!} currency={price.currency} />
          {saleUnit && <SaleUnitCaption variant="large" colour="light-foreground-alt">{saleUnit}</SaleUnitCaption>}
        </LuxPlusAmount>
        <StandardAmount variant="small" className="is-muted">
          <FormatCurrency value={price.price} currency={price.currency} />
        </StandardAmount>
      </Group>
    )
  }

  return (
    <Group direction="vertical" gap={2}>
      <Caption variant="large" colour="light-foreground-alt">
        {fromLabel}
      </Caption>
      <StandardAmount variant="medium" weight="bold">
        <FormatCurrency value={price.price} currency={price.currency} />
        {saleUnit && <SaleUnitCaption variant="large" colour="light-foreground-alt">{saleUnit}</SaleUnitCaption>}
      </StandardAmount>
    </Group>
  )
}

export default TripListRowPrice
